import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

// Types
import type { Office } from "types";

// UI (your form component)
import AgentForm from "./AgentsAddEditComponent";

// Toast
import { showSuccess, showError } from "toast/toast";
import { getCloudFileById, fileToBase64 } from "components/Imageshelper";
import { getAuthCookie } from "../utils/authCookies";
// Field validations (same ones you use today)
import {
  firstNameValidation,
  lastNameValidation,
  emailValidation,
  phoneValidation,
  phone2Validation,
  passwordValidation,
  statusValidation,
  addressLine1Validation,
  cityValidation,
  stateValidation,
  zipcodeValidation,
} from "validationSchemas/common";

// ---------- Helpers ----------
const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

type AgentAPI = {
  id?: string;
  _id?: string;
  membership_id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone1?: string;
  phone2?: string;
  status?: string;
  jobTitle?: string;
  profilePic?: string | null;
  brokerageFirmID?: string;
  organization_id?: string;
  officeIDs?: string[];
  office_ids?: string[];
  assigned_warehouse_ids?: string[];
  local_license_type?: string;
  local_user_type?: string;
  membership_type?: string;
  address?: Record<string, unknown>;
  professional_details?: Partial<{
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  agentDetails?: Partial<{
    brokerageFirmAffiliation: string;
    brokerageFirmID: string;
    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;
  }>;
  firmDetails?: {
    address?: Record<string, unknown>;
  };
  addresses?: {
    personal?: {
      address_details?: Record<string, unknown>;
    };
  };
  offices?: Array<{ _id?: string; id?: string }>;
  organization?: {
    _id?: string;
    id?: string;
    name?: string;
    profile?: { companyName?: string; company_name?: string };
    firmDetails?: { companyName?: string };
  };
};

const getOrganizationDisplayName = (org: unknown): string => {
  if (!org || typeof org !== "object") return "";
  const o = org as Record<string, any>;
  return String(
    o?.profile?.companyName ||
      o?.profile?.company_name ||
      o?.firmDetails?.companyName ||
      o?.name ||
      o?.organization?.profile?.companyName ||
      o?.organization?.name ||
      ""
  ).trim();
};

const normalizeAgentStatus = (status?: string) => {
  const upper = (status || "").toUpperCase();
  if (upper === "ACTIVE") return "Active";
  if (upper === "INACTIVE") return "Inactive";
  return status || "Active";
};

const getAgentPersonalAddress = (agent: AgentAPI | null) =>
  agent?.addresses?.personal?.address_details ||
  agent?.address ||
  agent?.firmDetails?.address;

const getAgentOfficeId = (agent: AgentAPI | null) => {
  if (Array.isArray(agent?.office_ids) && agent.office_ids.length > 0) {
    return String(agent.office_ids[0] || "").trim();
  }
  if (Array.isArray(agent?.officeIDs) && agent.officeIDs.length > 0) {
    return String(agent.officeIDs[0] || "").trim();
  }
  return String(agent?.offices?.[0]?._id || agent?.offices?.[0]?.id || "").trim();
};

const getAgentMembershipId = (agentData: AgentAPI | null, fallback = "") =>
  String(agentData?.membership_id || agentData?._id || agentData?.id || fallback).trim();

const toCoordinate = (value: unknown) => {
  const coordinate = Number(value ?? 0);
  return Number.isFinite(coordinate) ? coordinate : 0;
};

const toUrlString = (res: unknown): string | undefined => {
  if (!res) return undefined;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : undefined;
};

// ---------- Single Upsert Container ----------
const RealtorAddEditAgent: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const isEdit = !!agentId;

  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const token = getAuthCookie("sessionToken") || "";
  const userName = getAuthCookie("userName") || "";
  const organizationId = getAuthCookie("organizationId");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [agent, setAgent] = useState<AgentAPI | null>(null);
  const [serverImageSrc, setServerImageSrc] = useState<string | undefined>(undefined);
  const [cloudFileId, setCloudFileId] = useState<string | null>(null);
  const [brokerageFirmDetails, setBrokerageFirmDetails] = useState<AgentAPI["organization"] | null>(null);

  // Parent firm org from session (no /parent-hierarchy).
  const parentBrokerageId = String(organizationId || "").trim() || null;
  const parentBrokerageName = null as string | null;

  const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
  const buildAbsoluteFromRelative = (p: string) => {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return `${base}/${normalizePath(p)}`;
  };

  useEffect(() => {
    let active = true;

    (async () => {
      const val = agent?.profilePic;
      if (!val) {
        if (active) setServerImageSrc(undefined);
        return;
      }

      if (/^https?:\/\//i.test(val)) {
        if (active) setServerImageSrc(val);
        return;
      }

      if (/^[0-9a-f]{24}$/i.test(val)) {
        setCloudFileId(val);
        try {
          const res = await getCloudFileById(val, token as string);
          const u = toUrlString(res);
          if (active) setServerImageSrc(u);
        } catch {
          if (active) setServerImageSrc(undefined);
        }
        return;
      }

      if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
    })();

    return () => {
      active = false;
    };
  }, [agent?.profilePic, token]); // ⬅️ removed serverImageSrc from deps

  // Fetch brokerage firm details to preview company name
  useEffect(() => {
    (async () => {
      if (!organizationId || !token) return;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/full-details`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        if (res.ok) {
          const org = data?.organization || data?.data || data;
          setBrokerageFirmDetails(org);
        }
      } catch (err: any) {
        console.error("Failed to load brokerage firm details:", err);
      }
    })();
  }, [organizationId, token]);

  // Load offices (both add + edit)
  useEffect(() => {
    if (!token || !organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/organizations/${organizationId}/offices`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await res.json();
        if (!res.ok || data?.error) {
          throw new Error(data?.error || data?.message || "Failed to load offices");
        }
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.results)
              ? data.results
              : Array.isArray(data?.offices)
                ? data.offices
                : [];
        if (!cancelled) setOffices(list);
      } catch (err: any) {
        if (!cancelled) showError(err?.message || "Failed to load offices");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, organizationId]);

  // Load agent (edit only)
  useEffect(() => {
    (async () => {
      if (!isEdit || !agentId) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/organization-memberships/${agentId}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || data?.message || "Failed to fetch agent data");
        }
        if (data) setAgent(data as AgentAPI);
      } catch (error: any) {
        showError(error?.message || "Failed to fetch agent data");
      }
    })();
  }, [isEdit, agentId, token]);

  // Options for selects
  const officeOptions = useMemo(
    () =>
      (offices || [])
        .map((o: any) => ({
          value: String(o?._id ?? o?.id ?? "").trim(),
          label: String(o?.officeName ?? o?.name ?? "").trim() || "Office",
        }))
        .filter((o) => o.value),
    [offices]
  );

  // Brokerage firm options - preview logged-in brokerage (realtor / teammember)
  const brokerageFirmName = useMemo(() => {
    return (
      getOrganizationDisplayName(brokerageFirmDetails) ||
      getOrganizationDisplayName(agent?.organization) ||
      agent?.agentDetails?.brokerageFirmAffiliation ||
      (isTeammember ? parentBrokerageName : "") ||
      userName ||
      ""
    );
  }, [brokerageFirmDetails, agent, isTeammember, parentBrokerageName, userName]);

  const brokerageFirmOptions = useMemo(() => {
    if (!brokerageFirmName) return [];
    return [{ value: brokerageFirmName, label: brokerageFirmName }];
  }, [brokerageFirmName]);

  // Validation schema (email/password required only on Add)
  const validationSchema = useMemo(() => {
    const base = {
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      phone1: phoneValidation,
      phone2: phone2Validation,
      officeId: Yup.string().trim(),
      // addressLine1: addressLine1Validation,
      // city: cityValidation,
      // state: stateValidation,
      // zipcode: zipcodeValidation,
      // statuss: statusValidation,
    };

    if (isEdit) {
      return Yup.object(base);
    } else {
      return Yup.object({
        ...base,
        email: emailValidation,
        // password: passwordValidation,
      });
    }
  }, [isEdit]);

  const DEFAULT_TEN_ZEROS = "0000000000";
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;
  const initialValues = useMemo(() => {
    const addr = getAgentPersonalAddress(agent);
    const brokerageFirmId =
      (isTeammember ? parentBrokerageId : organizationId) ||
      agent?.brokerageFirmID ||
      agent?.organization_id ||
      agent?.organization?._id ||
      agent?.organization?.id ||
      "";

    return {
      firstName: agent?.firstName || "",
      lastName: agent?.lastName || "",
      phone1: agent?.phone1 || DEFAULT_TEN_ZEROS,
      phone2: agent?.phone2 || "",
      email: agent?.email || "",
      password: "",
      officeId: getAgentOfficeId(agent),
      agentID: agent?.professional_details?.agentID || agent?.agentDetails?.agentID || "",
      licenseNumber:
        agent?.professional_details?.licenseNumber || agent?.agentDetails?.licenseNumber || "",
      licenseExpiryDate:
        agent?.professional_details?.licenseExpiryDate || agent?.agentDetails?.licenseExpiryDate || "",
      // Do not seed from brokerageFirmDetails here — that loads async and would
      // reinitialize the form (wiping officeId) via enableReinitialize.
      brokerageFirmAffiliation:
        getOrganizationDisplayName(agent?.organization) ||
        agent?.agentDetails?.brokerageFirmAffiliation ||
        "",
      brokerageFirmID: brokerageFirmId,
      addressLine1: String(addr?.addressLine1 || ""),
      addressLine2: String(addr?.addressLine2 || ""),
      city: String(addr?.city || ""),
      state: String(addr?.state || ""),
      zipcode: String(addr?.zip || ""),
      cityCode: String(addr?.cityCode || ""),
      stateCode: String(addr?.stateCode || ""),
      country: String(addr?.country || US_COUNTRY_DEFAULTS.country),
      countryCode: String(addr?.countryCode || US_COUNTRY_DEFAULTS.countryCode),
      latitude: toCoordinate(addr?.latitude),
      longitude: toCoordinate(addr?.longitude),
      statuss: normalizeAgentStatus(agent?.status),
      image: null as File | null,
      displaySrc: agent?.profilePic || null,
      profilePic: agent?.profilePic || null,
    };
  }, [agent, isTeammember, organizationId, parentBrokerageId]);

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setFormSubmitting(true);
      try {
        if (!organizationId) {
          throw new Error("Organization ID is missing");
        }

        const profilePicPayload =
          values.image instanceof File ? await fileToBase64(values.image) : undefined;

        // const brokerageOrganizationId = String(
        //   (isTeammember ? parentBrokerageId : organizationId) ||
        //     (values as { brokerageFirmID?: string }).brokerageFirmID ||
        //     ""
        // ).trim();

        const agentMembershipId = getAgentMembershipId(agent, agentId || "");

        if (isEdit && !agentMembershipId) {
          throw new Error("Agent membership ID is missing");
        }

        const addressCountry = values.country?.trim() || US_COUNTRY_DEFAULTS.country;
        const addressCountryCode = values.countryCode?.trim() || US_COUNTRY_DEFAULTS.countryCode;
        const professionalDetails = {
          ...(values.agentID ? { agentID: values.agentID } : {}),
          ...(values.licenseNumber ? { licenseNumber: values.licenseNumber } : {}),
          ...(values.licenseExpiryDate ? { licenseExpiryDate: values.licenseExpiryDate } : {}),
        };

        const payload: Record<string, unknown> = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone1: withDefault(values.phone1),
          phone2: values.phone2 || "",
          jobTitle: agent?.jobTitle || "Agent",
          profilePic: profilePicPayload ?? String(values.profilePic || agent?.profilePic || ""),
          status: (values.statuss || "Active").toUpperCase(),
          local_license_type: "BROKERAGE_AGENT",
          local_user_type: "brokerage_agent",
          membership_type: "AGENT",
          office_ids: values.officeId ? [values.officeId] : [],
          assigned_warehouse_ids: [],
          address: {
            label: "Agent Address",
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2 || "",
            city: values.city,
            state: values.state,
            country: addressCountry,
            zip: values.zipcode,
            cityCode: values.cityCode || values.city,
            stateCode: values.stateCode,
            countryCode: addressCountryCode,
            latitude: toCoordinate(values.latitude),
            longitude: toCoordinate(values.longitude),
            phone: withDefault(values.phone1),
          },
          professional_details: professionalDetails,
        };

        const url = isEdit
          ? `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/memberships/agents/${agentMembershipId}`
          : `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/memberships/agents`;
        const method = isEdit ? "PATCH" : "POST";

        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });

        const result = await response.json();
        if (!response.ok || result?.error) {
          throw new Error(result?.error || result?.message || "Failed to save agent");
        }

        showSuccess(isEdit ? "Agent updated successfully!" : "Agent added successfully!");
        navigate(
          `${baseRoute}/agents`,
          isEdit ? undefined : { state: { successMessage: "Agent added successfully!" } }
        );
      } catch (err: any) {
        showError(err?.message || (isEdit ? "Failed to update Agent." : "Failed to add Agent."));
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // Seed brokerage firm affiliation so the preview shows the current firm
  useEffect(() => {
    if (!brokerageFirmName) return;
    if (formik.values.brokerageFirmAffiliation === brokerageFirmName) return;
    formik.setFieldValue("brokerageFirmAffiliation", brokerageFirmName);
  }, [brokerageFirmName]);

  // When offices load on create, prefer the default office if none selected yet
  useEffect(() => {
    if (isEdit || formik.values.officeId || officeOptions.length === 0) return;
    const defaultOffice = (offices as any[]).find((o) => o?.default_office || o?.defaultOffice);
    const preferred =
      String(defaultOffice?._id ?? defaultOffice?.id ?? "").trim() ||
      (officeOptions.length === 1 ? officeOptions[0].value : "");
    if (preferred) {
      formik.setFieldValue("officeId", preferred);
    }
  }, [isEdit, offices, officeOptions, formik.values.officeId]);

  // Derived options for AgentForm
  const STATUS_OPTIONS = React.useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
    ],
    []
  );

  // Handlers passed straight through
  const handleCancel = () => navigate(-1);
  const handleSubmit = () => formik.handleSubmit();
  const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };

  const displaySrc: string | undefined =
    serverImageSrc ??
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : undefined);

  return (
    <AgentForm
      isEdit={isEdit}
      formik={formik}
      brokerageFirmOptions={brokerageFirmOptions}
      officeOptions={officeOptions}
      statusOptions={STATUS_OPTIONS}
      displaySrc={displaySrc}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitting={formSubmitting}
      type="Brokerage_Agent"
    />
  );
};

export default RealtorAddEditAgent;
